import { Chip, Tooltip } from '@mui/material';
import { useTranslate } from 'react-admin';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import ArchiveIcon from '@mui/icons-material/Archive';
import { TicketStatus } from '../../types/chat.types';

export interface TicketStatusBadgeProps {
  status: TicketStatus;
  clickable?: boolean;
  onClick?: () => void;
  size?: 'small' | 'medium';
  /** Render only the icon (no label chip). Tooltip shows the status name on hover. */
  iconOnly?: boolean;
  /**
   * Show tooltip with status description.
   * Set to false in contexts where the status is already obvious
   * (e.g., in a "Pending" tab showing only pending tickets).
   * @default true
   */
  showTooltip?: boolean;
}

const STATUS_CONFIG: Record<
  TicketStatus,
  {
    color: 'info' | 'success' | 'default';
    icon: React.ElementType;
  }
> = {
  [TicketStatus.OPEN]: {
    color: 'info',
    icon: RadioButtonCheckedIcon,
  },
  [TicketStatus.RESOLVED]: {
    color: 'success',
    icon: CheckCircleOutlineIcon,
  },
  [TicketStatus.CLOSED]: {
    color: 'default',
    icon: ArchiveIcon,
  },
};

export function TicketStatusBadge({
  status,
  clickable = false,
  onClick,
  size = 'small',
  iconOnly = false,
  showTooltip = true,
}: TicketStatusBadgeProps) {
  const translate = useTranslate();

  const config = STATUS_CONFIG[status] || STATUS_CONFIG[TicketStatus.OPEN];
  const StatusIcon = config.icon;

  const label = translate(`resources.ticket.status.${status}`);
  const tooltipText = translate(`resources.ticket.statusDescription.${status}`);

  if (iconOnly) {
    const iconSize = size === 'small' ? 16 : 20;
    const icon = (
      <StatusIcon
        sx={{ fontSize: iconSize, color: `${config.color}.main`, cursor: clickable ? 'pointer' : 'default' }}
        onClick={clickable ? onClick : undefined}
      />
    );
    return (
      <Tooltip title={label} arrow placement="top">
        {icon}
      </Tooltip>
    );
  }

  const chip = (
    <Chip
      icon={<StatusIcon fontSize="small" />}
      label={label}
      size={size}
      color={config.color}
      variant="filled"
      clickable={clickable}
      onClick={clickable ? onClick : undefined}
      sx={{
        fontWeight: 500,
        cursor: clickable ? 'pointer' : 'default',
        '& .MuiChip-icon': {
          fontSize: size === 'small' ? 16 : 20,
        },
      }}
    />
  );

  if (showTooltip) {
    return (
      <Tooltip title={tooltipText} arrow placement="top">
        {chip}
      </Tooltip>
    );
  }

  return chip;
}

export default TicketStatusBadge;
